/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/ //
import { IModuleStateManager, IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { getAvailabilitiesForCartLineItems, ProductAvailabilitiesForCartLineItems } from '@msdyn365-commerce-modules/retail-actions';
import { Heading, IModuleProps, INodeProps, Waiting } from '@msdyn365-commerce-modules/utilities';
import { AsyncResultStatus, getUrlSync } from '@msdyn365-commerce/core';
import { Cart } from '@msdyn365-commerce/retail-proxy';
import { AttributeValueBase } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classnames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IFarmlandsCheckoutData } from './farmlands-checkout.data';
import { IFarmlandsCheckoutProps } from './farmlands-checkout.props.autogenerated';
import AlertComponent from './components/alert';
import { getLineItems, ILineItems } from './components/get-line-items';
import { getOrderSummary, IOrderSummary } from './components/get-order-summary';
import GuidedFormComponent from './components/guided-form';
import PlaceOrderButtonComponent from './components/place-order-button';
import placeOrder from './methods/place-order';
import { getContactDetailsAsync } from '../../actions/farmlands-get-contact-details-calls.action';
import { registerContactForBulkEmailsAsync } from '../../actions/farmlands-register-contact-for-bulk-emails-calls.action';
import {
    hasEcommerceAccess,
    isShareholderAccountDisabled,
    isShareholderAccountNotInactive,
    isShareholderAccountNotOnHold,
    isShareholder,
    isGuest,
    isCasual
} from '../../common/method/functions';
import { SHAREHOLDER_KEY_VALUE } from '../farmlands-checkout-shareholder-payment/farmlands-checkout-shareholder-payment';
import { CHECKOUT_ADDRESS_FLAG } from '../../helpers/esam-validation-helpers';
export * from './components/get-line-items';
export * from './components/get-order-summary';

type DeviceType = 'Mobile';

interface ICheckoutState {
    errorMessage: string;
    isBulkEmailChecked?: boolean;
    orderNote: string;
    hasEcomPermission: boolean;
    loaded: boolean;
}

export interface ICheckoutViewProps extends IFarmlandsCheckoutProps<IFarmlandsCheckoutData> {
    className: string;
    canShow?: boolean;
    isEmptyCart?: boolean;
    isConsentGiven?: boolean;
    hasError?: boolean;
    cartStatus?: string;
    isMobile?: boolean;
    isEditorialMode?: boolean;
    backToShoppingUrl?: string;
    editCartLink?: string;
    hasSalesOrder: boolean;

    checkoutProps: IModuleProps;
    headerProps: INodeProps;
    bodyProps: INodeProps;
    mainProps: INodeProps;
    mainControlProps: INodeProps;
    sideProps: INodeProps;
    sideControlFirstProps: INodeProps;
    sideControlSecondProps: INodeProps;

    guidedForm?: React.ReactNode;
    title?: React.ReactNode;
    loading?: React.ReactNode;
    alert?: React.ReactNode;
    orderSummary?: IOrderSummary;
    lineItems?: ILineItems;
    placeOrderButton?: React.ReactNode;
    keepShoppingButton?: React.ReactNode;
    orderConfirmation?: React.ReactNode;
}

export interface ISalesOrderAttribute extends AttributeValueBase {
    TextValue?: string;
    TextValueTranslations?: [];
}

export interface ICheckoutModuleProps extends IFarmlandsCheckoutProps<IFarmlandsCheckoutData>, IModuleStateProps {}

/**
 *
 * CheckoutContainer component
 * @extends {React.Component<ICheckoutModuleProps>}
 */
// @ts-ignore
@withModuleState
@observer
class Checkout extends React.Component<ICheckoutModuleProps> {
    @computed get cartStatus(): AsyncResultStatus {
        return get(this.props, 'data.checkout.status');
    }

    @computed get isEmptyCart(): boolean {
        if (this.isEditorialMode) {
            // Editorial mode: Ignore empty cart
            return false;
        }
        const cart = get(this.props, 'data.checkout.result.checkoutCart.cart');

        return this.cartStatus === 'SUCCESS' && !!cart && !isEmpty(cart) && (!cart.CartLines || cart.CartLines.length === 0);
    }

    @computed get hasError(): boolean {
        const cart = get(this.props, 'data.checkout.result.checkoutCart.cart');
        return this.cartStatus === 'FAILED' || (this.cartStatus === 'SUCCESS' && (!cart || isEmpty(cart)));
    }

    @computed get isEditorialMode(): boolean {
        return get(this.props, 'context.request.params.isEditor');
    }

    @computed get canPlaceOrder(): boolean {
        return this.props.moduleState.isReady && this.state.errorMessage === '';
    }

    @computed get canAccessCheckout(): boolean {
        const shareholderData = this.props.data.checkout.result?.checkoutCart.cart.ExtensionProperties!;
        if (shareholderData) {
            return !isShareholderAccountDisabled(shareholderData) && isShareholderAccountNotOnHold(shareholderData);
        }
        return true;
    }

    @computed get canAccessShareholderCheckout(): boolean {
        const cartExtensionProps = this.props.data.checkout.result?.checkoutCart.cart.ExtensionProperties || [];
        if (this.props.data.checkout.result?.checkoutCart) {
            return !(
                (!isShareholderAccountNotInactive(cartExtensionProps) || !isShareholderAccountNotOnHold(cartExtensionProps)) &&
                this.props.context.request.url.requestUrl.pathname === '/checkout-shareholder-only'
            );
        }

        // Return true if cart state hasn't been populated yet but checkout status (cartStatus) is still loading / succeeding.
        return this.cartStatus === 'SUCCESS' || this.cartStatus === 'LOADING';
    }

    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    @computed public get accountNumber(): Readonly<string | undefined> {
        return this.props.data.accountInformation.result?.AccountNumber;
    }

    public state: ICheckoutState = {
        errorMessage: '',
        isBulkEmailChecked: undefined,
        orderNote: '',
        hasEcomPermission: true,
        loaded: false
    };

    public async componentDidMount(): Promise<void> {
        const {
            resources: { genericErrorMessage }
        } = this.props;
        const sessionUserOrderNote = sessionStorage.getItem('order_note');
        const cartObj = (await this.props.data.checkout).checkoutCart.cart;
        const deliveryMethod = cartObj.DeliveryMode;

        if (isShareholder(cartObj)) {
            (Window as any).digitalData.transaction.profile.profileInfo.customerType = 'shareholder';
        } else if (isCasual(cartObj)) {
            (Window as any).digitalData.transaction.profile.profileInfo.customerType = 'casual';
        } else if (isGuest(cartObj)) {
            (Window as any).digitalData.transaction.profile.profileInfo.customerType = 'guest';
        }

        // Delivery method for analytics
        if (deliveryMethod === this.props.app.config.deliveryModeIdDeliveryValue) {
            (Window as any).digitalData.transaction.profile.deliveryMethod = 'ship';
        } else if (deliveryMethod === this.props.app.config.deliveryModeIdPickupValue) {
            (Window as any).digitalData.transaction.profile.deliveryMethod = 'pick-up';
            if (cartObj.CartLines) {
                (Window as any).digitalData.transaction.profile.fulfillmentStoreId = cartObj.CartLines[0].FulfillmentStoreId;
            }
        }
        if (cartObj.CartLines && cartObj.CartLines.length > 0) {
            cartObj.CartLines.forEach((product) => {
                let productObj = {
                    productInfo: {
                        productName: product.Description,
                        productId: product.ProductId,
                        sku: product.ItemId,
                        unitPrice: product.TotalAmount && product.Quantity ? product.TotalAmount / product.Quantity : 0,
                        price: product.TotalAmount,
                        quantity: product.Quantity
                    }
                };
                (Window as any).digitalData.product.push(productObj);
                (Window as any).digitalData.transaction.profile.item.push(productObj);
            });
        }

        if (sessionUserOrderNote) {
            const userOrderNote = JSON.parse(sessionUserOrderNote);
            this.setState({ orderNote: userOrderNote.orderNote });
        }

        // @ts-ignore: Compiler not reconizing condition check for function params
        when(
            () => this.hasError,
            () => {
                this.setState({
                    errorMessage: genericErrorMessage
                });
                this.props.context.telemetry.error('Failed to get cart');
            }
        );

        // @ts-ignore: Compiler not reconizing condition check for function params
        when(
            () => {
                return this.isEmptyCart;
            },
            () => {
                const editCartLink = getUrlSync('cart', this.props.context.actionContext) || '';
                if (window && editCartLink) {
                    // Redirect to cart page if cart is empty
                    window.location.assign(editCartLink);
                } else {
                    this.setState({
                        errorMessage: genericErrorMessage
                    });
                    this.props.context.telemetry.error('Cart is empty, but no editCartLink found');
                }
            }
        );

        reaction(
            () => this.props.moduleState.isReady,
            () => {
                this.onContainerReady();
            }
        );

        // check bulk email opt in status for logged in users
        if (this.props.context.request.user.token) {
            const userContactDetails = await getContactDetailsAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                JSON.parse(atob(this.props.context.request.user.token.split('.')[1])).oid
            );
            const isOptedInBulkEmails = userContactDetails?.SendBulkEmails;

            if (isOptedInBulkEmails !== 'Allowed') {
                this.setState({
                    isBulkEmailChecked: false
                });
            }

            if (!hasEcommerceAccess(userContactDetails)) {
                this.setState({
                    hasEcomPermission: false
                });
            }
        }

        when(
            () => {
                return !this.canAccessShareholderCheckout || !this.canAccessCheckout || !this.state.hasEcomPermission;
            },
            () => {
                const editCartLink = getUrlSync('cart', this.props.context.actionContext) || '';
                if (window && editCartLink) {
                    window.location.assign(editCartLink);
                } else {
                    this.props.context.telemetry.error('Cart is empty, but no editCartLink found');
                }
            }
        );
    }

    // tslint:disable-next-line:max-func-body-length
    public render(): JSX.Element {
        const {
            moduleState,
            config: { className, checkoutHeading, disableGuidedCheckoutFlow, bulkEmailMessage },
            resources
        } = this.props;
        const { errorMessage } = this.state;
        const { placeOrderText, cookieConsentRequiredMessage, genericErrorMessage } = resources;
        const checkoutClass = classnames('ms-checkout', className);
        const checkoutInformation = this.getSlotItems('checkoutInformation');
        const orderNoteMaxCharacter = 200;

        const isConsentGiven =
            this.props.context.request &&
            this.props.context.request.cookies &&
            this.props.context.request.cookies.isConsentGiven &&
            this.props.context.request.cookies.isConsentGiven();

        const isMobile =
            this.props.context.request &&
            this.props.context.request.device &&
            (this.props.context.request.device.Type as DeviceType) === 'Mobile';

        const editCartLink = getUrlSync('cart', this.props.context.actionContext) || '';

        let viewProps: ICheckoutViewProps = {
            ...this.props,
            className: checkoutClass,
            isEmptyCart: this.isEmptyCart,
            isConsentGiven,
            hasError: this.hasError,
            hasSalesOrder: this.props.data.checkout && this.props.data.checkout.result !== undefined && this.props.data.checkout.result.salesOrder !== undefined,
            cartStatus: this.cartStatus,
            isMobile,
            editCartLink,
            isEditorialMode: this.isEditorialMode,
            orderConfirmation:
                this.props.slots.orderConfirmation && this.props.slots.orderConfirmation.length > 0
                    ? this.props.slots.orderConfirmation[0]
                    : undefined,
            checkoutProps: { moduleProps: this.props, className: checkoutClass },
            headerProps: { className: 'ms-checkout__head' },
            bodyProps: { className: 'ms-checkout__body' },
            mainProps: { className: 'ms-checkout__main' },
            mainControlProps: { className: 'ms-checkout__main-control' },
            sideProps: { className: 'ms-checkout__side' },
            sideControlFirstProps: { className: 'ms-checkout__side-control-first' },
            sideControlSecondProps: { className: 'ms-checkout__side-control-second' },
            title: checkoutHeading && <Heading {...checkoutHeading} className='ms-checkout__title' />
        };

        if (isConsentGiven === false) {
            viewProps = {
                ...viewProps,
                alert: <AlertComponent {...{ message: cookieConsentRequiredMessage }} />
            };
        } else if (!this.cartStatus || this.cartStatus === 'LOADING' || this.isEmptyCart) {
            viewProps = {
                ...viewProps,
                loading: <Waiting className='msc-waiting-circular msc-waiting-lg' />
            };
        } else if (this.hasError) {
            viewProps = {
                ...viewProps,
                alert: <AlertComponent {...{ message: genericErrorMessage }} />
            };
        } else if (!this.canAccessShareholderCheckout || !this.canAccessCheckout || !this.state.hasEcomPermission) {
            viewProps = {
                ...viewProps,
                alert: <AlertComponent {...{ message: 'Authorize access required' }} />
            };
        } else {
            viewProps = {
                ...viewProps,
                alert: errorMessage && <AlertComponent {...{ message: errorMessage }} />,
                canShow: true,
                guidedForm: checkoutInformation ? (
                    <GuidedFormComponent
                        {...{
                            items: checkoutInformation,
                            moduleState: moduleState,
                            disableGuidedCheckoutFlow: disableGuidedCheckoutFlow,
                            resource: resources,
                            isMobile: isMobile,
                            isEditor: this.isEditorialMode
                        }}
                    />
                ) : undefined,
                orderSummary: getOrderSummary(this.props, this.displayName, this.accountNumber),
                lineItems: getLineItems(this.props),
                placeOrderButton: (
                    <>
                        <span className={'ms-checkout__order-note-label'}>Order notes (optional)</span>
                        <textarea
                            className='ms-checkout-order-note__input-text form-control'
                            id='order-note'
                            placeholder='Notes about your order, eg. special notes for delivery.'
                            value={this.state.orderNote}
                            onChange={this.handleOrderNoteChange}
                            onBlur={this.handleOrderNoteBlur}
                            maxLength={orderNoteMaxCharacter}
                        />
                        <div className='ms-checkout-order-note-limit-ctn'>
                            {orderNoteMaxCharacter - this.state.orderNote.length}/{orderNoteMaxCharacter}
                        </div>
                        {this.state.isBulkEmailChecked !== undefined && (
                            <div className='ms-checkout__bulk-email'>
                                {/* tslint:disable-next-line: react-a11y-role-has-required-aria-props */}
                                <input
                                    type='checkbox'
                                    id='bulk-email'
                                    checked={this.state.isBulkEmailChecked}
                                    onChange={this.handleBulkEmailChange}
                                />
                                <label htmlFor='bulk-email'>{bulkEmailMessage}</label>
                            </div>
                        )}
                        <PlaceOrderButtonComponent
                            {...{
                                placeOrderText,
                                placeOrder: this.onPlaceOrder,
                                canPlaceOrder: this.canPlaceOrder,
                                registerForBulkEmails: this._registerForBulkEmails
                            }}
                        />
                    </>
                )
            };
        }
        if (!this.state.loaded && this.props.data.products.result?.length) {
            window.dispatchEvent(new CustomEvent('CheckoutView'));
            this.state.loaded = true;
        }
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _registerForBulkEmails = async (): Promise<void | undefined> => {
        if (this.state.isBulkEmailChecked) {
            return registerContactForBulkEmailsAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                JSON.parse(atob(this.props.context.request.user.token.split('.')[1])).oid
            );
        }
        return;
    };

    private _updateAttributeArray = async (input: any): Promise<void> => {
        if (this.props.data.checkout.result) {
            const cart = this.props.data.checkout.result?.checkoutCart.cart;
            const cartProperties = [];
            if (cart?.ExtensionProperties?.find((prop) => prop.Key === SHAREHOLDER_KEY_VALUE)) {
                cartProperties.push({
                    Key: SHAREHOLDER_KEY_VALUE,
                    Value: {
                        BooleanValue: true
                    }
                });
            }
            await this.props.data.checkout.result?.checkoutCart.updateAttributeValues({ newAttributeValues: input });

            if (cartProperties.length > 0) {
                this.props.data.checkout.result?.checkoutCart.cart.ExtensionProperties!.push({
                    Key: SHAREHOLDER_KEY_VALUE,
                    Value: {
                        BooleanValue: true
                    }
                });
            }
        }
    };
    private handleOrderNoteBlur = (): void => {
        const sessionOrderNote = {
            orderNote: this.state.orderNote
        };
        sessionStorage.setItem('order_note', JSON.stringify(sessionOrderNote));
    };

    private orderNoteUpdate = (): void => {
        const cart = this.props.data.checkout.result?.checkoutCart.cart;
        const cartAttributeArray = cart?.AttributeValues || [];
        const salesOrderAttribute: ISalesOrderAttribute = cartAttributeArray?.find((obj) => obj.Name === 'Special instruction') || {};
        if (salesOrderAttribute && salesOrderAttribute.TextValue) {
            salesOrderAttribute.TextValue = this.state.orderNote;
        } else {
            const attributeObj = {
                // @ts-ignore
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                Name: 'Special instruction',
                ExtensionProperties: [],
                TextValue: this.state.orderNote,
                TextValueTranslations: []
            };
            attributeObj.Name = 'Special instruction';
            cartAttributeArray?.push(attributeObj);
        }
        if (this.props.data.checkout.result?.checkoutCart.cart.ShippingAddress?.ExtensionProperties) {
            const checkoutAddressFlag = this.props.data.checkout.result?.checkoutCart.cart.ShippingAddress?.ExtensionProperties?.find(
                (obj) => obj.Key === CHECKOUT_ADDRESS_FLAG
            );
            if (checkoutAddressFlag) {
                const attributeObj = {
                    // @ts-ignore
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                    Name: 'CheckoutAddressFlag',
                    ExtensionProperties: [],
                    TextValue: checkoutAddressFlag.Value?.StringValue,
                    TextValueTranslations: []
                };
                attributeObj.Name = 'CheckoutAddressFlag';
                cartAttributeArray?.push(attributeObj);
            }
        }
    };

    private retrieveSessionStoreAttribute = (sessionStorageValueKey: string, attributeName: string): void => {
        const sessionStorageValue = sessionStorage.getItem(sessionStorageValueKey);
        const cart = this.props.data.checkout.result?.checkoutCart.cart;
        const cartAttributeArray = cart?.AttributeValues || [];

        const salesOrderAttribute: ISalesOrderAttribute = cartAttributeArray?.find((obj) => obj.Name === attributeName) || {};

        // If no session storage value exists, abort function.
        if (!sessionStorageValue) {
            return;
        }

        if (salesOrderAttribute && salesOrderAttribute.TextValue) {
            // Update attribute if it exists.
            salesOrderAttribute.TextValue = sessionStorageValue;
        } else {
            // Else create new attribute.
            const attributeObj = {
                // @ts-ignore
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                Name: attributeName,
                ExtensionProperties: [],
                TextValue: sessionStorageValue,
                TextValueTranslations: []
            };
            cartAttributeArray?.push(attributeObj);
        }

        // Clear sessionStorage value once finished.
        window.sessionStorage.removeItem(sessionStorageValueKey);
    };

    private handleOrderNoteChange = (e: any): void => {
        this.setState({ orderNote: e.target.value });
    };

    private handleBulkEmailChange = (): void => {
        this.setState({ isBulkEmailChecked: !this.state.isBulkEmailChecked });
    };

    private getSlotItems = (key: string): React.ReactNode[] | undefined => {
        const { slots } = this.props;
        return slots && slots[key] && slots[key].length ? slots[key] : undefined;
    };

    private onPlaceOrder = async (): Promise<void> => {
        const {
            resources: { checkoutOutOfStockErrorMessage, genericErrorMessage },
            context: {
                actionContext,
                app: {
                    config: { enableStockCheck = false }
                }
            },
            slots: { orderConfirmation },
            data: { checkout }
        } = this.props;
        if (enableStockCheck && (await this.isOverMaxQuantity((await checkout).checkoutCart.cart))) {
            this.setState({
                errorMessage: checkoutOutOfStockErrorMessage
            });
            this.props.context.telemetry.error(checkoutOutOfStockErrorMessage);
        } else {
            window.sessionStorage.removeItem('selectedDeliveryMethod');

            // Add additional attribute values to the final cart data through the following function calls
            this.orderNoteUpdate();
            this.retrieveSessionStoreAttribute('phone', 'Phone Number');
            this.retrieveSessionStoreAttribute('customerName', 'Customer Name');

            const hasOrderConfirmation = orderConfirmation && orderConfirmation.length > 0;

            // Update attribute array if any of the following attributes exist.
            const cartAttributeArray = this.props.data.checkout.result?.checkoutCart.cart.AttributeValues;
            const salesOrderAttribute = cartAttributeArray?.find((obj) => obj.Name === 'Special instruction');
            const checkoutAddressAttribute = cartAttributeArray?.find((obj) => obj.Name === 'CheckoutAddressFlag');
            const phoneAttribute = cartAttributeArray?.find((obj) => obj.Name === 'Phone Number');
            const customerNameAttribute = cartAttributeArray?.find((obj) => obj.Name === 'Customer Name');
            const inventoryAvaailabilityMessage = cartAttributeArray?.find((obj) => obj.Name!.includes('Inventory availability '));

            if (
                salesOrderAttribute ||
                checkoutAddressAttribute ||
                phoneAttribute ||
                customerNameAttribute ||
                inventoryAvaailabilityMessage
            ) {
                await this._updateAttributeArray([
                    salesOrderAttribute,
                    checkoutAddressAttribute,
                    phoneAttribute,
                    customerNameAttribute,
                    inventoryAvaailabilityMessage
                ]);
            }

            // tslint:disable-next-line: no-floating-promises
            this.populateDataLayer();

            await placeOrder(actionContext, this.props.data.checkout.result, this.props.data.products.result, !hasOrderConfirmation).catch(
                (error) => {
                    this.setState({
                        errorMessage: genericErrorMessage
                    });
                    this.props.telemetry.exception(error);
                }
            );
        }
    };

    // return true if any product in the cart is over its max quantity
    private isOverMaxQuantity = async (cart: Cart): Promise<boolean> => {
        let isOverMaxQuantity = false;

        await getAvailabilitiesForCartLineItems(
            new ProductAvailabilitiesForCartLineItems(this.props.context.actionContext.requestContext.apiSettings),
            this.props.context.actionContext
        )
            .then((productInventoryInformation) => {
                cart.CartLines?.map((cartline) => {
                    if (productInventoryInformation && productInventoryInformation.length > 0) {
                        const foundProductAvailability = productInventoryInformation.find((productInventory) => {
                            return productInventory.ProductAvailableQuantity?.ProductId! === cartline.ProductId;
                        });

                        if (
                            !(
                                foundProductAvailability?.IsProductAvailable &&
                                foundProductAvailability.ProductAvailableQuantity.AvailableQuantity !== undefined &&
                                cartline.Quantity &&
                                cartline.Quantity <= foundProductAvailability.ProductAvailableQuantity.AvailableQuantity
                            )
                        ) {
                            isOverMaxQuantity = true;
                        }
                    }
                });
            })
            .catch((error) => {
                this.props.context.actionContext.telemetry.exception(error);
                throw new Error('[getAvailabilitiesForCartLineItems] Unable to get availabilities for products in checkout cart');
            });

        return isOverMaxQuantity;
    };

    private getActiveChildModuleStates = (): IModuleStateManager[] => {
        const { childIds, getModule } = this.props.moduleState;
        return childIds
            .map(getModule)
            .filter((state): state is IModuleStateManager => !!state && !state.isDisabled && state.hasModuleState);
    };

    private onContainerReady = (): void => {
        this.getActiveChildModuleStates().forEach((state) => {
            state.isReady && state.onContainerReady && state.onContainerReady();
        });
    };

    private populateDataLayer = async () => {
        const cart = await get(this.props, 'data.checkout.result.checkoutCart.cart');
        if (!cart) {
            return;
        }
        const cartInfo = cart.CartLines;
        const cartObj = { transactionTotal: '', totalItems: undefined, transactionProducts: [{}] };
        const itemArr: Object[] = [];
        cartObj.transactionTotal = cart.AmountDue;
        cartObj.totalItems = cart.TotalItems;
        for (let i = 0; i < cartInfo.length; i++) {
            itemArr.push({
                name: cartInfo[i].Description,
                sku: cartInfo[i].ItemId,
                price: cartInfo[i].Price,
                quantity: cartInfo[i].Quantity,
                netPrice: cartInfo[i].NetPrice
            });
        }
        cartObj.transactionProducts = itemArr;
        sessionStorage.setItem('cart_gtm', JSON.stringify(cartObj));
    };
}
export default Checkout;
